import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Card, CardContent, Button, Badge, EmptyState, Skeleton, ErrorState } from '@/components/ui'
import { AppWindow, Plus, Users, ArrowRight, MoreVertical } from 'lucide-react'
import { api, type AppSummaryInfo } from '@/api/client'

export function AppsListPage() {
  const { orgSlug } = useParams()
  const [apps, setApps] = useState<AppSummaryInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchApps = async () => {
      if (!orgSlug) return

      try {
        setError(null)
        const response = await api.apps.list(orgSlug)
        setApps(response.apps)
      } catch (err) {
        console.error('Failed to fetch apps:', err)
        setError(err instanceof Error ? err.message : 'Failed to load applications')
      } finally {
        setIsLoading(false)
      }
    }

    fetchApps()
  }, [orgSlug])

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Apps"
          description="Manage your applications"
          action={
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              Create App
            </Button>
          }
        />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div>
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-48 mb-2" />
                      <div className="flex gap-4">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Apps"
          description="Manage your applications"
          action={
            <Button asChild>
              <Link to={`/orgs/${orgSlug}/apps/new`}>
                <Plus className="mr-2 h-4 w-4" />
                Create App
              </Link>
            </Button>
          }
        />
        <ErrorState
          title="Failed to load applications"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Apps"
        description="Manage your applications"
        action={
          <Button asChild>
            <Link to={`/orgs/${orgSlug}/apps/new`}>
              <Plus className="mr-2 h-4 w-4" />
              Create App
            </Link>
          </Button>
        }
      />

      {apps.length === 0 ? (
        <EmptyState
          icon={AppWindow}
          title="No apps yet"
          description="Create your first app to get started"
          action={
            <Button asChild>
              <Link to={`/orgs/${orgSlug}/apps/new`}>
                <Plus className="mr-2 h-4 w-4" />
                Create App
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {apps.map((app) => (
            <Card key={app.id} className="group">
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <Link
                    to={`/orgs/${orgSlug}/apps/${app.slug}`}
                    className="flex items-center gap-4"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)]">
                      {app.logo_url ? (
                        <img src={app.logo_url} alt={app.name} className="h-8 w-8 object-contain" />
                      ) : (
                        <AppWindow className="h-6 w-6 text-[var(--text-secondary)]" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-[var(--text-primary)]">
                          {app.name}
                        </h3>
                        <Badge
                          variant={app.status === 'active' ? 'success' : 'default'}
                        >
                          {app.status}
                        </Badge>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-[var(--text-muted)]">
                        <span className="font-mono">{app.slug}</span>
                        <span>Created {new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      asChild
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Link to={`/orgs/${orgSlug}/apps/${app.slug}`}>
                        Open <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
